import type { ResizeCursor } from '@meterup/atto';
import {
  DropdownMenu,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPopover,
  DropdownMenuTrigger,
  ResizeCursorShield,
  styled,
  useUtilityVisibility,
  UtilityControl,
} from '@meterup/atto';
import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { FlexiColumnsControlsContainer } from './FlexiColumns.styles';

// Portaled: the container's transform and inline-size containment would otherwise offset `fixed`.
const CursorAnchor = styled('button', {
  position: 'fixed',
  width: 0,
  height: 0,
  padding: 0,
  border: 'none',
  background: 'none',
});
CursorAnchor.displayName = 'FlexiColumnsControlsCursorAnchor';

export interface FlexiColumnsControlsProps {
  autoHide?: boolean;
  startLabel: string;
  endLabel: string;
  startWidth: number;
  startCollapsible?: boolean;
  endCollapsible?: boolean;
  resizable?: boolean;
  resizeBounds: { min: number; max: number };
  resizeCursor: ResizeCursor;
  isResizing: boolean;
  isDefaultWidth: boolean;
  containerRef: React.RefObject<HTMLElement | null>;
  onCollapseStart: () => void;
  onCollapseEnd: () => void;
  onResizeStart: (e: React.PointerEvent) => void;
  onResizeKeyDown: (e: React.KeyboardEvent) => void;
  onResetWidth: () => void;
}

export function FlexiColumnsControls({
  autoHide = true,
  startLabel,
  endLabel,
  startWidth,
  startCollapsible = false,
  endCollapsible = false,
  resizable = true,
  resizeBounds,
  resizeCursor,
  isResizing,
  isDefaultWidth,
  containerRef,
  onCollapseStart,
  onCollapseEnd,
  onResizeStart,
  onResizeKeyDown,
  onResetWidth,
}: FlexiColumnsControlsProps) {
  const { min, max } = resizeBounds;
  const { visible } = useUtilityVisibility({
    containerRef: autoHide ? containerRef : undefined,
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const resizeHandleRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <FlexiColumnsControlsContainer
        direction="column"
        placement="sides"
        relation="joined"
        variant="neutral"
        visible={autoHide ? visible || isMenuOpen : undefined}
        opaque={!autoHide}
      >
        {startCollapsible && (
          <UtilityControl
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              onCollapseStart();
            }}
            aria-label={`Collapse ${startLabel}`}
            icon="pane-start-collapse"
            label={`Collapse ${startLabel.toLowerCase()}`}
          />
        )}
        {endCollapsible && (
          <UtilityControl
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              onCollapseEnd();
            }}
            aria-label={`Collapse ${endLabel}`}
            icon="pane-end-collapse"
            label={`Collapse ${endLabel.toLowerCase()}`}
          />
        )}
        {resizable && (
          <UtilityControl
            ref={resizeHandleRef}
            role="slider"
            aria-valuenow={startWidth}
            aria-valuemin={min}
            aria-valuemax={max}
            aria-label="Resize columns"
            aria-orientation="horizontal"
            onPointerDown={(e: React.PointerEvent) => {
              e.stopPropagation();
              if (e.button !== 0) return;
              onResizeStart(e);
            }}
            onContextMenu={(e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              setMenuPosition({ x: e.clientX, y: e.clientY });
              setIsMenuOpen(true);
            }}
            onKeyDown={onResizeKeyDown}
            style={{ touchAction: 'none', cursor: resizeCursor }}
            icon="drag-vertical"
            label="Resize"
          />
        )}
        <ResizeCursorShield cursor={isResizing ? resizeCursor : null} />
      </FlexiColumnsControlsContainer>
      {resizable && (
        <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
          {createPortal(
            <DropdownMenuTrigger
              aria-label="Resize options"
              tabIndex={-1}
              render={<CursorAnchor style={{ left: menuPosition.x, top: menuPosition.y }} />}
            />,
            document.body,
          )}
          <DropdownMenuPopover align="start" sideOffset={0} finalFocus={resizeHandleRef}>
            <DropdownMenuGroup>
              <DropdownMenuItem
                icon="reset"
                label="Reset"
                onClick={onResetWidth}
                disabled={isDefaultWidth}
                tooltip={isDefaultWidth ? 'Already at the default width' : undefined}
              />
            </DropdownMenuGroup>
          </DropdownMenuPopover>
        </DropdownMenu>
      )}
    </>
  );
}
